import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Users, Mail, Phone, Calendar, MessageSquare, Star
} from 'lucide-react';
import { Button } from '../../components/ui/button';

import { API_URL } from '../../config/api';

export default function TeamPage() {
  const { user } = useOutletContext();
  const [assistants, setAssistants] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTeam();
  }, []);

  const fetchTeam = async () => {
    const token = localStorage.getItem('client_token');
    if (!token) return;

    try {
      const response = await fetch(`${API_URL}/api/client/users/${user.id}/assistants`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setAssistants(data);
      }
    } catch (error) {
      console.error('Error fetching team:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="team-loading">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-rm-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="team-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-rm-text">Mon Équipe</h1>
        <p className="text-rm-text-secondary">Vos assistants dédiés pour toi accompagner</p>
      </div>

      {/* Team Grid */}
      {assistants.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-12 text-center">
          <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-rm-text mb-2">Aucun assistant assigné</h3>
          <p className="text-rm-text-secondary mb-4">
            Contacte-nous pour être mis en relation avec un assistant à mi-temps ou plein temps.
          </p>
          <Button className="bg-rm-primary hover:bg-rm-primary/90">
            <Mail className="w-4 h-4 mr-2" />
            Nous contacter
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {assistants.map((assistant, index) => (
            <motion.div
              key={assistant.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden"
              data-testid={`team-member-${assistant.id}`}
            >
              {/* Header with avatar */}
              <div className="bg-gradient-to-r from-rm-primary to-rm-secondary p-6 text-white">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center text-2xl font-bold">
                    {assistant.name?.charAt(0) || 'A'}
                  </div>
                  <div>
                    <h3 className="text-xl font-bold">{assistant.name}</h3>
                    <p className="text-white/80 text-sm capitalize">
                      {assistant.role === 'assistant' ? 'Assistant(e)' : assistant.role}
                    </p>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-6 space-y-4">
                {assistant.email && (
                  <div className="flex items-center gap-3 text-sm">
                    <Mail className="w-4 h-4 text-rm-primary" />
                    <a href={`mailto:${assistant.email}`} className="text-rm-text hover:text-rm-primary">
                      {assistant.email}
                    </a>
                  </div>
                )}
                
                {assistant.phone && (
                  <div className="flex items-center gap-3 text-sm">
                    <Phone className="w-4 h-4 text-rm-primary" />
                    <a href={`tel:${assistant.phone}`} className="text-rm-text hover:text-rm-primary">
                      {assistant.phone}
                    </a>
                  </div>
                )}

                {assistant.specialties && assistant.specialties.length > 0 && (
                  <div className="flex flex-wrap gap-2 pt-2">
                    {assistant.specialties.map((specialty, idx) => (
                      <span
                        key={idx}
                        className="text-xs bg-rm-primary/10 text-rm-primary px-2 py-1 rounded-full"
                      >
                        {specialty}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="p-4 border-t border-slate-100 flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="flex-1"
                  onClick={() => window.location.href = `/espace-client/messages`}
                >
                  <MessageSquare className="w-4 h-4 mr-1" />
                  Message
                </Button>
                <Button 
                  size="sm" 
                  className="flex-1 bg-rm-primary hover:bg-rm-primary/90"
                  onClick={() => window.location.href = `/espace-client/calendar`}
                >
                  <Calendar className="w-4 h-4 mr-1" />
                  RDV
                </Button>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Info Card */}
      <div className="bg-rm-primary/5 rounded-xl p-6 border border-rm-primary/20">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 bg-rm-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
            <Star className="w-5 h-5 text-rm-primary" />
          </div>
          <div>
            <h3 className="font-semibold text-rm-text mb-1">Besoin d'aide ?</h3>
            <p className="text-sm text-rm-text-secondary">
              Vos assistants sont disponibles du lundi au vendredi, de 9h à 18h. 
              N'hésitez pas à leur envoyer un message ou à planifier un rendez-vous 
              directement depuis l'agenda partagé.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
